import React, { useContext, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn'
import SaveIcon from '@mui/icons-material/Save'
import moment from 'moment'
import AnDateTimePicker from '../../../../../components/DateTime/DateTimePicker'
import { ControlRoomContext } from '../ControlPage'
import API from '../../../../../services/API'

const fieldOrder = [
  'assigned',
  'mobile',
  'arriveScene',
  'leaveScene',
  'arriveHospital',
  'patientHandover',
  'clear',
]

const fields = [
  { field: 'assigned', label: 'Assigned' },
  { field: 'mobile', label: 'Mobile' },
  { field: 'arriveScene', label: 'Arrive Scene' },
  { field: 'leaveScene', label: 'Leave Scene' },
  { field: 'arriveHospital', label: 'Arrive Hospital' },
  { field: 'patientHandover', label: 'Patient Handover' },
  { field: 'clear', label: 'Clear' },
]

const TimeField = ({ time, label, handleChange, field, times }) => {
  const currentIndex = fieldOrder.indexOf(field)
  const laterFields = fieldOrder.slice(currentIndex + 1)
  const laterHasAnyValue = laterFields.some((f) => !!times?.[f])
  const disabled = !time && !laterHasAnyValue

  return (
    <Grid item xs={12} md={6}>
      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          borderRadius: 3,
          border: disabled
            ? '1px solid #E2E8F0'
            : '1px solid rgba(35,154,164,0.24)',
          backgroundColor: disabled ? '#F8FAFC' : '#FFFFFF',
        }}
      >
        <Stack spacing={1}>
          <Stack direction="row" spacing={1} alignItems="center">
            <AccessTimeIcon
              sx={{
                color: disabled ? '#94A3B8' : '#239AA4',
                fontSize: 20,
              }}
            />
            <Typography
              fontWeight={900}
              color={disabled ? 'text.secondary' : '#242F40'}
            >
              {label}
            </Typography>

            {time && (
              <Chip
                size="small"
                label={moment(time).format('HH:mm')}
                sx={{
                  ml: 'auto',
                  borderRadius: 999,
                  backgroundColor: '#E6F7F9',
                  color: '#1E7F88',
                  fontWeight: 900,
                }}
              />
            )}
          </Stack>

          <AnDateTimePicker
            date={time ?? null}
            disableFuture
            label={label}
            fullWidth
            onChange={(newDate) =>
              handleChange(moment(newDate).toISOString(), field)
            }
            disabled={disabled}
          />

          {disabled && (
            <Typography variant="caption" color="text.secondary">
              Complete a later time first to edit this skipped step.
            </Typography>
          )}
        </Stack>
      </Paper>
    </Grid>
  )
}

const ResourceTimesPopup = ({
  callSign,
  originalTimes,
  assignedResourceRowID,
  onClose,
}) => {
  const { selectedIncident, setSelectedIncident, selectedControlRoom } =
    useContext(ControlRoomContext)

  const [times, setTimes] = useState(originalTimes ?? {})
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(false)

  useEffect(() => {
    setTimes(originalTimes ?? {})
    setSaving(false)
    setError(false)
  }, [assignedResourceRowID, originalTimes])

  const changed = useMemo(() => {
    return fields.some(({ field }) => {
      const originalValue = originalTimes?.[field]
        ? moment(originalTimes[field]).toISOString()
        : null

      const currentValue = times?.[field]
        ? moment(times[field]).toISOString()
        : null

      return originalValue !== currentValue
    })
  }, [originalTimes, times])

  const completedCount = useMemo(
    () => fields.filter(({ field }) => !!times?.[field]).length,
    [times],
  )

  const handleChange = (newDate, field) => {
    setTimes((prev) => ({
      ...prev,
      [field]: newDate,
    }))
  }

  const handleSubmitChanges = async () => {
    if (saving || !selectedIncident?.id || !assignedResourceRowID) return

    setSaving(true)
    setError(false)

    try {
      const res = await API.post('/commandcontrol/updateresourcealltimes', {
        incidentID: selectedIncident.id,
        assignedResourceRowID,
        newTimes: times,
        controlRoomID: selectedControlRoom,
      })

      if (res.status === 200) {
        setSelectedIncident((prev) => {
          if (!prev) return prev

          return {
            ...prev,
            assignedResources: (prev.assignedResources ?? []).map((r) =>
              r._id === assignedResourceRowID
                ? {
                    ...r,
                    times: {
                      ...r.times,
                      ...times,
                    },
                  }
                : r,
            ),
          }
        })

        onClose()
      } else {
        setError(true)
      }
    } catch {
      setError(true)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Box sx={{ width: '100%', minWidth: { md: 760 } }}>
      <Box
        sx={{
          mx: -3,
          mt: -3,
          mb: 3,
          p: 5,
          color: 'white',
          background:
            'linear-gradient(135deg, #1A2230 0%, #242F40 55%, #239AA4 150%)',
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255,255,255,0.12)',
              border: '1px solid rgba(255,255,255,0.22)',
            }}
          >
            <AssignmentTurnedInIcon sx={{ fontSize: 30 }} />
          </Box>

          <Stack flex={1}>
            <Typography variant="overline" sx={{ opacity: 0.72 }}>
              Resource Timeline
            </Typography>

            <Typography variant="h5" fontWeight={900} color="white">
              {callSign || 'Resource'}
            </Typography>

            <Typography variant="body2" sx={{ opacity: 0.78 }} color="white">
              Update assignment and movement times for this incident.
            </Typography>
          </Stack>

          <Chip
            label={`${completedCount}/${fields.length} completed`}
            sx={{
              borderRadius: 999,
              backgroundColor: 'rgba(255,255,255,0.14)',
              color: 'white',
              border: '1px solid rgba(255,255,255,0.2)',
              fontWeight: 900,
            }}
          />
        </Stack>
      </Box>

      <Stack spacing={2.5}>
        {error && (
          <Alert severity="error" sx={{ borderRadius: 3 }}>
            Unable to update resource times. Please try again.
          </Alert>
        )}
        <Box>
          <Grid container spacing={1.5}>
            {fields.map(({ field, label }) => (
              <TimeField
                key={field}
                handleChange={handleChange}
                time={times?.[field]}
                label={label}
                field={field}
                times={times}
              />
            ))}
          </Grid>
        </Box>

        <Divider />

        <Stack direction={{ xs: 'column-reverse', sm: 'row' }} spacing={1.5}>
          <Button
            fullWidth
            onClick={onClose}
            disabled={saving}
            sx={{
              borderRadius: 999,
              textTransform: 'none',
              fontWeight: 800,
              color: '#64748B',
            }}
          >
            Cancel
          </Button>

          <Button
            fullWidth
            variant="contained"
            onClick={handleSubmitChanges}
            disabled={!changed || saving}
            endIcon={
              saving ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <SaveIcon />
              )
            }
            sx={{
              borderRadius: 999,
              textTransform: 'none',
              fontWeight: 900,
              backgroundColor: '#239AA4',
              boxShadow: '0 12px 28px rgba(35,154,164,0.28)',
              '&:hover': {
                backgroundColor: '#1E7F88',
                boxShadow: '0 16px 34px rgba(35,154,164,0.34)',
              },
            }}
          >
            Save Timeline
          </Button>
        </Stack>
      </Stack>
    </Box>
  )
}

export default ResourceTimesPopup
